// src/components/GoalListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import DetailedListView from './DetailedListView';
import { goalQueries, initiativeQueries, updateQueries, staffQueries } from '../repository/graphQueries'; 
import { formatDate, formatDateTime, formatScore } from '../utils';
import { GOAL_STATUS_OPTIONS, INITIATIVE_STATUS_OPTIONS, DRI_EXPLANATION } from '../constants'; 
import { RiInformationLine as InformationCircleIcon } from 'react-icons/ri';

const DropdownCheckbox = ({ options, selectedOptions, onChange, title }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="relative inline-block text-left">
            <div>
                <button type="button" className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" onClick={() => setIsOpen(!isOpen)}>
                    {title}
                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </button>
            </div>
            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {options.map(option => (
                            <label key={option} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                <input type="checkbox" className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" checked={selectedOptions.includes(option)} onChange={() => onChange(option)} />
                                <span className="ml-3">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    return (
        <div className="relative inline-block">
            <div className="inline-flex items-center cursor-help" onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} onClick={() => setIsVisible(!isVisible)}>
                {children}
            </div>
            {isVisible && (
                <div className="absolute top-full right-0 mt-2 w-72 p-3 text-sm bg-gray-800 text-white rounded-md shadow-lg z-50">
                    {content}
                    <div className="absolute bottom-full right-2 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
};

const cleanDriList = (rawDriList) => {
    if (!rawDriList) return [];
    const separators = / and | & | \/ | \\ | with | for |,| featuring | assisting | cc /i;
    const allNames = rawDriList.flatMap(dri => {
        if (typeof dri !== 'string') return [];
        const cleaned = dri.replace(/\(.*\)|\[.*\]/g, '');
        return cleaned.split(separators);
    });
    const finalDris = allNames
        .map(name => name.trim())
        .filter(name => name && name.length > 1);
    return [...new Set(finalDris)].sort();
};

const goalColumns = [
    { header: 'Name', key: 'name' },
    { header: 'DRI', key: 'assigned_to'},
    { header: 'Status', key: 'status' },
    { header: 'Initiatives', key: 'initiativeCount', align: 'center' },
    { header: 'Blockers', key: 'blockingIssuesCount', align: 'center'},
    {
        header: 'Priority Score',
        key: 'score',
        align: 'center',
        formatter: formatScore
    },
    { header: 'Last Communication', key: 'lastCommunicationDate', formatter: formatDateTime },
    { header: 'Description', key: 'description', type: 'longText' },
];

const initiativeColumns = [
    { header: 'Name', key: 'name'},
    { header: 'DRI', key: 'assigned_to'},
    { header: 'Status', key: 'status'},
    { header: 'Supported Goals', key: 'supportedGoals', formatter: val => Array.isArray(val) ? val.join(', ') : val},
    { header: 'Blockers', key: 'blockingIssuesCount', align: 'center'},
    { header: 'Action Items', key: 'actionItemCount', align: 'center'},
    {
        header: 'Momentum Score',
        key: 'score',
        align: 'center',
        formatter: formatScore
    },
    { header: 'Description', key: 'description', type: 'longText'},
];


const GoalListView = () => {
    const { neo4jService, clientId } = useOutletContext();

    const [goalsList, setGoalsList] = useState([]);
    const [loadingGoals, setLoadingGoals] = useState(false);
    const [errorGoals, setErrorGoals] = useState(null);

    const [initiativesList, setInitiativesList] = useState([]);
    const [loadingInitiatives, setLoadingInitiatives] = useState(false);
    const [errorInitiatives, setErrorInitiatives] = useState(null);
    
    const [dris, setDris] = useState([]);
    const [loadingDris, setLoadingDris] = useState(false);
    const [selectedDris, setSelectedDris] = useState([]);
    const driTooltipContent = DRI_EXPLANATION;

    const loadDris = useCallback(async () => {
        if (!neo4jService || !clientId) return;
        setLoadingDris(true);
        try {
            const driResult = await neo4jService.runQuery(staffQueries.getDistinctDRIsForClient, { clientId });
            const rawDriList = driResult.map(r => r.dri);
            setDris(cleanDriList(rawDriList));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        } finally {
            setLoadingDris(false);
        }
    }, [neo4jService, clientId]);


    const loadGoalsList = useCallback(async () => {
        if (!neo4jService || !clientId) return;
        setLoadingGoals(true); setErrorGoals(null);
        try {
            const d = await neo4jService.runQuery(goalQueries.getAllGoalsList, { 
                clientId, 
                driFilter: selectedDris.length > 0 ? selectedDris : null 
            });
            setGoalsList(d);
        } catch (e) {
            setErrorGoals(e.message); setGoalsList([]);
        } finally { setLoadingGoals(false); }
    }, [neo4jService, clientId, selectedDris]);

    const loadInitiativesList = useCallback(async () => {
         if (!neo4jService || !clientId) return;
         setLoadingInitiatives(true); setErrorInitiatives(null);
         try {
             const d = await neo4jService.runQuery(initiativeQueries.getAllInitiativesList, { 
                clientId,
                driFilter: selectedDris.length > 0 ? selectedDris : null
             });
             setInitiativesList(d);
         } catch (e) {
             setErrorInitiatives(e.message); setInitiativesList([]);
         } finally { setLoadingInitiatives(false); }
    }, [neo4jService, clientId, selectedDris]);

    useEffect(() => {
        if (clientId) {
            loadDris();
        }
    }, [clientId, loadDris]);

    useEffect(() => {
        if (clientId) {
            loadGoalsList();
            loadInitiativesList();
        }
    }, [clientId, selectedDris, loadGoalsList, loadInitiativesList]);
    
    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const handleGoalUpdate = (itemId, updatedField, newValue) => {
        console.log(`Goal ${itemId} field ${updatedField} updated to ${newValue}. Refreshing list.`);
        loadGoalsList();
    };
    const handleInitiativeUpdate = (itemId, updatedField, newValue) => {
        console.log(`Initiative ${itemId} field ${updatedField} updated to ${newValue}. Refreshing list.`);
        loadInitiativesList();
    };

    const goalEditableColumnsConfig = {
        status: {
            options: GOAL_STATUS_OPTIONS,
            query: updateQueries.updateNodeStatus,
            propertyName: 'status'
        }
    };
    const initiativeEditableColumnsConfig = {
         status: {
            options: INITIATIVE_STATUS_OPTIONS,
            query: updateQueries.updateNodeStatus,
            propertyName: 'status'
        }
    };

    return (
        <>
            <div className="flex justify-between items-center mb-6">
                 <h2 className="text-xl font-semibold">Goals & Initiatives</h2>
                 <div className="flex items-center space-x-2">
                    <DropdownCheckbox
                        options={dris}
                        selectedOptions={selectedDris}
                        onChange={handleDriChange}
                        title="Filter by DRI"
                    />
                    <Tooltip content={driTooltipContent}>
                        <InformationCircleIcon className="h-4 w-4 text-gray-500 cursor-help" />
                    </Tooltip>
                 </div>
            </div>
            <DetailedListView
                title="Goals"
                items={goalsList}
                columns={goalColumns}
                isLoading={loadingGoals}
                error={errorGoals}
                entityType="goals"
                clientId={clientId}
            />
            <DetailedListView
                title="Initiatives"
                items={initiativesList}
                columns={initiativeColumns}
                isLoading={loadingInitiatives}
                error={errorInitiatives}
                entityType="initiatives"
                clientId={clientId}
                neo4jService={neo4jService} 
                onItemUpdate={handleInitiativeUpdate} 
                editableColumnsConfig={initiativeEditableColumnsConfig} 
            />
        </>
    );
};

export default GoalListView;